import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Phone } from 'lucide-react';
import useSEO from '../hooks/useSEO';
import { SITE, AREAS } from '../content/site';
import { pageByPath, alternatesOf } from '../content';

// Textes de l'interface.
const UI = {
  fr: {
    home: 'Accueil',
    breadcrumb: "Fil d'Ariane",
    ask: 'Demander une information',
    more: 'En savoir plus',
    faq: 'Questions fréquentes',
    related: 'À lire aussi',
    ctaTitle: 'Une fuite, une infiltration, un sinistre ?',
    ctaText: "Décrivez-nous la situation : nous vous indiquons comment nous procéderions. Service d'urgence 24h/24.",
    ctaLink: 'Formulaire de contact',
    contact: '/contact',
  },
  de: {
    home: 'Startseite',
    breadcrumb: 'Brotkrümelnavigation',
    ask: 'Anfrage senden',
    more: 'Mehr erfahren',
    faq: 'Häufige Fragen',
    related: 'Weitere Informationen',
    ctaTitle: 'Ein Leck, eindringendes Wasser, ein Schaden?',
    ctaText: 'Beschreiben Sie uns die Situation: Wir sagen Ihnen, wie wir vorgehen würden. Notdienst rund um die Uhr.',
    ctaLink: 'Kontaktformular',
    contact: '/de/kontakt',
  },
  en: {
    home: 'Home',
    breadcrumb: 'Breadcrumb',
    ask: 'Request information',
    more: 'Read more',
    faq: 'Frequently asked questions',
    related: 'Related pages',
    ctaTitle: 'A leak, water ingress, an insurance claim?',
    ctaText: "Describe the situation and we'll tell you how we would proceed. 24/7 emergency service.",
    ctaLink: 'Contact form',
    contact: '/en/contact',
  },
};

// Lien vers les autres langues, rédigé dans la langue cible.
const SWITCH_LABELS = {
  fr: 'Cette page en français',
  de: 'Diese Seite auf Deutsch',
  en: 'This page in English',
};

// Transforme « [texte](/chemin) » en lien interne.
const RichText = ({ text }) => {
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (!m) return part;
    return (
      <Link key={i} to={m[2]} className="text-brand underline underline-offset-2 hover:text-brand-deep">
        {m[1]}
      </Link>
    );
  });
};

const stripLinks = (text) => text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');

const breadcrumbOf = (page, ui) => {
  const chain = [];
  let current = page;
  while (current) {
    chain.unshift(current);
    current = current.parent && pageByPath[current.parent];
  }
  return [{ path: '/', nav: ui.home }, ...chain];
};

const buildJsonLd = (page, crumbs) => {
  const url = `${SITE.url}${page.path}`;
  const graph = [
    {
      '@type': 'BreadcrumbList',
      itemListElement: crumbs.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: c.nav,
        item: `${SITE.url}${c.path}`,
      })),
    },
    page.guide
      ? {
          '@type': 'Article',
          '@id': `${url}#article`,
          headline: page.h1,
          description: page.description,
          url,
          inLanguage: page.lang || 'fr',
          datePublished: page.guide.datePublished,
          dateModified: page.guide.dateModified || page.guide.datePublished,
          author: { '@id': SITE.orgId },
          publisher: { '@id': SITE.orgId },
        }
      : {
          '@type': 'Service',
          '@id': `${url}#service`,
          name: page.h1,
          serviceType: page.serviceType || 'Recherche de fuite',
          description: page.description,
          url,
          provider: { '@id': SITE.orgId },
          areaServed: page.areaServed || AREAS.both,
        },
  ];
  if (page.faq && page.faq.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: page.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: stripLinks(f.a) },
      })),
    });
  }
  return { '@context': 'https://schema.org', '@graph': graph };
};

const Section = ({ section }) => {
  const ListTag = section.ordered ? 'ol' : 'ul';
  return (
    <section className="mb-14">
      <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-5">{section.h2}</h2>
      {(section.body || []).map((p, i) => (
        <p key={i} className="text-ink-soft leading-relaxed mb-4"><RichText text={p} /></p>
      ))}
      {section.list && (
        <ListTag className="space-y-3 my-6">
          {section.list.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              {section.ordered ? (
                <span className="font-serif font-semibold text-brand w-5 flex-shrink-0">{`${i + 1}.`}</span>
              ) : (
                <CheckCircle2 className="w-5 h-5 text-brand mt-0.5 flex-shrink-0" strokeWidth={1.6} />
              )}
              <span className="text-ink-soft leading-relaxed"><RichText text={item} /></span>
            </li>
          ))}
        </ListTag>
      )}
      {(section.after || []).map((p, i) => (
        <p key={i} className="text-ink-soft leading-relaxed mb-4"><RichText text={p} /></p>
      ))}
    </section>
  );
};

const Cards = ({ paths, ui }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
    {paths.map((path) => {
      const p = pageByPath[path];
      return (
        <Link key={path} to={path} className="group border-t-2 border-brand pt-5 block">
          <h3 className="font-serif text-xl font-semibold text-ink mb-2 group-hover:text-brand transition-colors">
            {p.nav}
          </h3>
          <p className="text-ink-soft leading-relaxed text-sm mb-3">{p.summary}</p>
          <span className="flex items-center gap-2 text-sm font-semibold text-brand">
            {ui.more} <ArrowRight className="w-4 h-4" />
          </span>
        </Link>
      );
    })}
  </div>
);

const SeoPage = ({ page }) => {
  const lang = page.lang || 'fr';
  const ui = UI[lang];
  const alternates = alternatesOf(page.path);
  useSEO({ title: page.title, description: page.description, path: page.path, lang, alternates });
  const crumbs = breadcrumbOf(page, ui);

  return (
    <div className="pt-36 pb-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildJsonLd(page, crumbs)) }}
      />
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-8">
          <nav aria-label={ui.breadcrumb} className="text-sm text-ink-faint">
            {crumbs.map((c, i) => (
              <span key={c.path}>
                {i > 0 && <span className="mx-2">/</span>}
                {i < crumbs.length - 1 ? (
                  <Link to={c.path} className="hover:text-ink">{c.nav}</Link>
                ) : (
                  <span className="text-ink-soft">{c.nav}</span>
                )}
              </span>
            ))}
          </nav>
          {alternates && (
            <div className="flex flex-wrap gap-x-5 gap-y-1">
              {Object.entries(alternates)
                .filter(([l]) => l !== lang)
                .map(([l, href]) => (
                  <Link
                    key={l}
                    to={href}
                    hrefLang={l}
                    lang={l}
                    className="text-sm text-brand underline underline-offset-2 hover:text-brand-deep"
                  >
                    {SWITCH_LABELS[l]}
                  </Link>
                ))}
            </div>
          )}
        </div>

        <header className={`grid grid-cols-1 gap-12 items-center mb-20 ${page.image ? 'lg:grid-cols-[1.1fr_0.9fr]' : ''}`}>
          <div className="max-w-3xl">
            <div className="text-xs font-semibold tracking-widest uppercase text-brand mb-4">{page.eyebrow}</div>
            <h1 className="font-serif text-4xl md:text-5xl font-semibold text-ink mb-6 leading-tight">{page.h1}</h1>
            {page.intro.map((p, i) => (
              <p key={i} className={`leading-relaxed mb-4 ${i === 0 ? 'text-lg text-ink' : 'text-ink-soft'}`}>
                <RichText text={p} />
              </p>
            ))}
            <div className="flex flex-wrap items-center gap-6 mt-6">
              <a href={SITE.phoneHref} className="inline-flex items-center gap-2 px-6 py-3 bg-ink text-white text-sm font-medium hover:bg-brand-deep transition-colors">
                <Phone className="w-4 h-4" />{SITE.phone}
              </a>
              <Link to={ui.contact} className="flex items-center gap-2 text-sm font-medium text-ink hover:text-brand transition-colors">
                {ui.ask} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          {page.image && (
            <img src={page.image} alt={page.imageAlt} className="w-full h-72 lg:h-80 object-cover rounded-sm" />
          )}
        </header>

        <div className="max-w-3xl">
          {page.sections.map((s, i) => <Section key={i} section={s} />)}
        </div>

        {page.children && (
          <section className="mt-6 mb-20">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-10">{page.childrenTitle}</h2>
            <Cards paths={page.children} ui={ui} />
          </section>
        )}

        {page.faq && page.faq.length > 0 && (
          <section className="max-w-3xl mt-6 mb-20">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-ink mb-10">{ui.faq}</h2>
            <div className="space-y-10">
              {page.faq.map((f, i) => (
                <div key={i} className="border-t-2 border-brand pt-5">
                  <h3 className="font-semibold text-ink mb-2">{f.q}</h3>
                  <p className="text-ink-soft leading-relaxed"><RichText text={f.a} /></p>
                </div>
              ))}
            </div>
          </section>
        )}

        {page.related && (
          <section className="mb-20">
            <h2 className="font-serif text-2xl font-semibold text-ink mb-10">{ui.related}</h2>
            <Cards paths={page.related} ui={ui} />
          </section>
        )}

        <section className="bg-ink text-white p-8 md:p-12 flex flex-col md:flex-row md:items-center gap-8">
          <div className="flex-1">
            <h2 className="font-serif text-2xl font-semibold mb-3">{ui.ctaTitle}</h2>
            <p className="text-white/70 leading-relaxed">{ui.ctaText}</p>
          </div>
          <div className="flex flex-col gap-3">
            <a href={SITE.phoneHref} className="inline-flex items-center gap-2 text-brand-cyan font-semibold">
              <Phone className="w-4 h-4" />{SITE.phone}
            </a>
            <Link to={ui.contact} className="inline-flex items-center gap-2 text-sm font-medium text-white hover:text-brand-cyan">
              {ui.ctaLink} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SeoPage;
